/**
 * Admin billing-cycle types.
 *
 * Mirrors `BillingCycleListDto` / `BillingCycleSummaryDto` in
 * `WiCloud.Admin.Application/Features/BillingCycles/DTOs`. Enum-backed fields use the backend's
 * PascalCase member names verbatim — the API accepts the same strings back as filters, so there is
 * no mapping layer (see frontend-conventions.md).
 */

export const BILLING_CYCLE_STATUSES = [
  "Active",
  "Paused",
  "Suspended",
  "Expired",
  "Cancelled",
] as const;

export type BillingCycleStatus = (typeof BILLING_CYCLE_STATUSES)[number];

export const BILLING_FREQUENCIES = [
  "Hourly",
  "Daily",
  "Monthly",
  "Quarterly",
  "Annually",
] as const;

export type BillingFrequency = (typeof BILLING_FREQUENCIES)[number];

/**
 * What funded a cycle, mirroring the backend `FundingSource` enum member for member.
 *
 * `Transfer` was missing while the backend has had it since ownership transfer shipped — a
 * transferred VM's cycle serialised to a string this union did not contain, so anything narrowing
 * on it fell through its cases while the value was on screen the whole time.
 */
export const FUNDING_SOURCES = ["Payment", "SignupBonus", "Transfer"] as const;

export type FundingSource = (typeof FUNDING_SOURCES)[number];

/**
 * How each funding source reads on screen. "Paid" / "Trial" is the distinction an operator is
 * actually making; the filter still sends the backend enum name, never the label.
 */
export const FUNDING_SOURCE_LABELS: Record<FundingSource, string> = {
  Payment: "Paid",
  SignupBonus: "Trial",
  Transfer: "Transfer",
};

/** One row of the admin billing-cycle list. Mirrors `BillingCycleListDto`. */
export interface BillingCycle {
  id: string;
  status: BillingCycleStatus;
  frequency: BillingFrequency;
  fundingSource: FundingSource;
  /** Order/renewal id for `Payment`, sign-up bonus lot id for `SignupBonus`. */
  sourceReferenceId: string | null;
  resourceId: string;
  /**
   * Null only when the resource row no longer exists (the cycle outlives it). A deleted machine
   * keeps its name.
   */
  resourceName: string | null;
  /**
   * Resource's current status, e.g. "Running". Null when the resource row is gone.
   * Returned by the API but not rendered in the list — kept so a future detail view can use it.
   */
  resourceStatus: string | null;
  /** Provider UUID. Null while the resource is Provisioning/Error, or when the row is gone. */
  providerResourceId: string | null;
  tenantId: string;
  tenantName: string | null;
  userId: string;
  userEmail: string | null;
  userPhoneNumber: string | null;
  cycleStart: string;
  /** What the resource is paid through. */
  cycleEnd: string;
  nextBillingDate: string;
  lastBillingDate: string | null;
  amount: number;
  currency: string;
  /**
   * Whole months the cycle covers — the unit `amount` is in, and the only honest answer to
   * "how long is this period". `frequency` cannot say it: its enum has no 24- or 36-month
   * member, so those cycles read "Monthly". 0 for a day-term cycle.
   */
  termMonths: number;
  autoRenew: boolean;
  /** > 0 means billing has been retried — the signal for a failed renewal. */
  billingAttempts: number;
  lastBillingAttemptAt: string | null;
  lastInvoiceId: string | null;
  pausedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BillingCycleFilters {
  /** Created-at range lower bound. Only sent together with `createdTo`. */
  createdFrom?: string;
  /** Created-at range upper bound. Only sent together with `createdFrom`. */
  createdTo?: string;
  /** Narrows to one resource's cycles. Omitted on the cross-tenant list. */
  resourceId?: string;
  /**
   * One term, routed by shape in `AdminBillingCycleRepository`: a whole GUID matches the cycle,
   * resource, tenant or provider VM UUID by equality; a whole e-mail matches the owner; anything
   * else is a substring over VM name, provider UUID, e-mail and phone.
   */
  search?: string;
  /**
   * Exact tenant id. The endpoint still accepts it, but the cross-tenant list no longer sends it:
   * its "Tenant ID" box was retired once `search` began routing a pasted GUID to the tenant arm.
   * Left here for a caller that wants the filter without the shape test.
   */
  tenantId?: string;
  /** Cycle status, as the backend enum member name. Omitted for "all statuses". */
  status?: BillingCycleStatus;
  /** What funded the cycle, as the backend enum member name. Omitted for "all funding". */
  fundingSource?: FundingSource;
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

/**
 * Body of `PUT /api/v1/admin/billing-cycles/{id}`. Mirrors `UpdateBillingCycleDto`.
 *
 * Dates are ISO-8601 strings; the backend reads an instant without an offset as UTC, so send one
 * with a `Z` (or a real offset) rather than a bare local timestamp.
 *
 * Only the billing record moves. The subscribed resource's own `expiresAt` is left where it is —
 * that has its own operator action on the resource screen.
 */
export interface UpdateBillingCycleRequest {
  cycleStart: string;
  cycleEnd: string;
  status: BillingCycleStatus;
}

/**
 * One machine whose expiry no longer matches the end of its active billing cycle.
 * Mirrors `BillingCycleExpiryMismatchDto`.
 *
 * The two are one date stored twice: the suspension scan reads the resource's `expiresAt`, while
 * the renewal quote and every billing report read the cycle's `cycleEnd`.
 */
export interface BillingCycleExpiryMismatch {
  /** The active cycle — what the edit screen is opened with, so it lands on this one. */
  billingCycleId: string;
  resourceId: string;
  resourceName: string;
  resourceStatus: string;
  /** Provider VM UUID. Null while the machine is Provisioning or Error. */
  providerResourceId: string | null;
  tenantId: string;
  tenantName: string | null;
  userEmail: string | null;
  userName: string | null;
  userPhone: string | null;
  cycleEnd: string;
  /**
   * Always set: a machine with no expiry has no second date to be out of step with, so the API
   * leaves it out of this report entirely.
   */
  resourceExpiresAt: string | null;
  /**
   * Signed distance in whole CALENDAR DAYS. Positive = the machine runs LONGER than the cycle paid
   * for; negative = it is cut short.
   *
   * Never 0: the pair is only a mismatch when the two calendar days differ, which is also why this
   * is not reported in hours — the detection has no sub-day precision to offer.
   */
  driftDays: number | null;
}

/**
 * One live machine, still inside its term, that no active billing cycle accounts for.
 * Mirrors `ResourceWithoutBillingCycleDto`.
 *
 * The case the mismatch report structurally cannot state: that one compares an expiry against a
 * cycle end, which needs both, so a machine with no cycle drops out of it rather than becoming a
 * finding. Nothing repairs these either — the nightly reconciliation only moves a resource onto a
 * cycle that already exists.
 */
export interface ResourceWithoutBillingCycle {
  resourceId: string;
  resourceName: string;
  resourceStatus: string;
  /** Always in the future: the API only returns machines still inside their term. */
  expiresAt: string;
  /**
   * Whole calendar days of term left; 0 means it ends today. Computed server-side against the same
   * clock the query filtered on, so the screen cannot disagree with the API about what is in term.
   */
  daysRemaining: number;
  /** Provider VM UUID. Null while the machine is in Error. */
  providerResourceId: string | null;
  tenantId: string;
  tenantName: string;
  userEmail: string;
  userName: string;
  userPhone: string;
  /** End of the last cycle this machine ever had, or null when it never had one. */
  lastCycleEnd: string | null;
  /** Status of that last cycle, or empty when there never was one. */
  lastCycleStatus: string;
}
